import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  IconButton,
  Button,
  alpha,
  Link,
  Alert,
  CircularProgress,
  Tooltip,
  Avatar,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  useTheme,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  LocationOn as LocationIcon,
  Router as RouterIcon,
  Storage as StorageIcon,
  Timeline as TimelineIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Pause as PauseIcon,
  Settings as SettingsIcon,
  CheckCircle,
  Warning,
  Block,
  PowerOff,
  Power,
  Close,
  WhatsApp as WhatsAppIcon,
  Notifications as NotificationsIcon,
  Person as PersonIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  ErrorOutline as ErrorOutlineIcon
} from '@mui/icons-material';

import { ftthCaixaService, FtthCaixa } from '../services/ftthCaixaService';
import CaixaMapComponent from './ftth/components/CaixaMapComponent';
import CaixaForm from '../components/forms/FtthCaixaCtoForm';
import FtthCaixaCeoForm from '../components/forms/FtthCaixaCeoForm';
import { getCaixaStatusColor, getCaixaStatusIcon, getCaixaStatusLabel } from '../utils/caixaStatusUtils';
import circuitService, { Circuito } from '../services/circuitService';
import CircuitoViewModal from '../components/modals/CircuitoViewModal';
import CtosModal from './ftth/olt/CtosModal';
import { detailPageHeaderPaperSx, tableHeadRowSx } from '../styles/theme';
import { MonitorStatusChip, formatMonitorDateTime, normalizeStatusLabel, getServiceStatusColor, getConnectionStatusColor } from '../utils/monitorDisplayUtils';
import { fetchCaixaPorts, enrichCaixaPortsConnection } from '../utils/loadCaixaPorts';
import { CaixaPort } from '../types/caixaPort';
import { GatewayWhatsappSelect } from '../components/selects/GatewayWhatsappSelect';
import { SysEditorWhatsapp } from '../components/syseditor';
import mensagemService from '../services/mensagemService';
import { contactService } from '../services/contactService';
import { Contact } from '../types';
import { enqueueSnackbar } from 'notistack';

const FtthCaixaDetailsPage: React.FC = () => {
  const theme = useTheme();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [caixa, setCaixa] = useState<FtthCaixa | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [caixaPorts, setCaixaPorts] = useState<CaixaPort[]>([]);
  const [loadingPorts, setLoadingPorts] = useState(false);
  const [disablePortModal, setDisablePortModal] = useState(false);
  const [selectedPortToDisable, setSelectedPortToDisable] = useState<number | null>(null);
  const [disableReason, setDisableReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [caixaFormDialog, setCaixaFormDialog] = useState<{
    open: boolean;
    caixa: FtthCaixa | null; // Revertido: volta a enviar objeto completo
  }>({
    open: false,
    caixa: null
  });
  const [occupiedPorts, setOccupiedPorts] = useState<number>(0);
  // Estados para circuitos livres
  const [circuitosLivres, setCircuitosLivres] = useState<Circuito[]>([]);
  const [loadingCircuitos, setLoadingCircuitos] = useState(false);

  // Estados para modal de circuito
  const [circuitoModal, setCircuitoModal] = useState(false);
  const [selectedCircuito, setSelectedCircuito] = useState<number | null>(null);

  const [ctosModalOpen, setCtosModalOpen] = useState(false);

  // Estados para modal de notificação de clientes
  const [notifyModalOpen, setNotifyModalOpen] = useState(false);
  const [notifyMensagem, setNotifyMensagem] = useState('');
  const [notifyGateway, setNotifyGateway] = useState<number | ''>(9999);
  const [notifyLoading, setNotifyLoading] = useState(false);
  const [notifyResults, setNotifyResults] = useState<{ clientName: string; success: boolean; error?: string }[]>([]);
  const [notifyStep, setNotifyStep] = useState<'form' | 'results'>('form');

  useEffect(() => {
    if (id) {
      loadCaixa();
    }
  }, [id]);

  // Carregar dados das portas quando Caixa for carregada
  useEffect(() => {
    if (caixa && caixa.id) {
      loadCaixaPorts(caixa.id);
      loadCircuitosLivres(caixa.id);
    }
  }, [caixa]);

  const loadCaixa = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!id) throw new Error('ID da Caixa não fornecido');

      console.log('🔍 Carregando caixa com ID:', id);
      const response = await ftthCaixaService.getById(parseInt(id));
      console.log('📡 Resposta do backend:', response);

      if (response.success && response.data) {
        // Corrigir disabled_ports se vier como string JSON
        let caixaData = { ...response.data };
        if (typeof caixaData.disabled_ports === 'string') {
          try {
            const parsedPorts = JSON.parse(caixaData.disabled_ports);
            // Corrigir formato dos objetos de porta desabilitada
            caixaData.disabled_ports = parsedPorts.map((dp: any) => ({
              port: dp.port,
              reason: dp.reason,
              disabledAt: dp.date || dp.disabledAt, // Backend usa 'date', frontend espera 'disabledAt'
              disabledBy: dp.usuarioId || dp.disabledBy // Backend usa 'usuarioId', frontend espera 'disabledBy'
            }));
          } catch (err) {
            console.warn('Erro ao parsear disabled_ports:', err);
            caixaData.disabled_ports = [];
          }
        }

        // Garantir que disabled_ports seja um array
        if (!Array.isArray(caixaData.disabled_ports)) {
          caixaData.disabled_ports = [];
        }

        // Converter coordenadas de string para number se necessário
        if (typeof caixaData.latitude === 'string' && caixaData.latitude) {
          caixaData.latitude = parseFloat(caixaData.latitude);
        }
        if (typeof caixaData.longitude === 'string' && caixaData.longitude) {
          caixaData.longitude = parseFloat(caixaData.longitude);
        }

        console.log('✅ Caixa processada:', caixaData);

        if (caixaData.tipo !== 'cto') {
          setError('Esta página é disponível apenas para caixas do tipo CTO.');
          setCaixa(null);
          return;
        }

        setCaixa(caixaData);
      } else {
        console.error('❌ Resposta inválida do backend:', response);
        throw new Error('Caixa não encontrada ou resposta inválida');
      }
    } catch (err: any) {
      console.error('❌ Erro ao carregar Caixa:', err);
      console.error('❌ Stack trace:', err.stack);
      setError(err.message || 'Erro ao carregar dados da Caixa');
    } finally {
      setLoading(false);
    }
  };

  const loadCaixaPorts = async (caixaId: number) => {
    try {
      setLoadingPorts(true);
      setError(null);

      const ports = await fetchCaixaPorts(caixaId);
      await enrichCaixaPortsConnection(ports);
      const occupiedCount = ports.filter((port) => port.status === 'occupied').length;

      setOccupiedPorts(occupiedCount);
      setCaixaPorts(ports);
    } catch (err: any) {
      console.error('Erro ao carregar portas da Caixa:', err);
      setError(err.message || 'Erro ao carregar portas da Caixa');

      if (caixa) {
        const basicPorts: CaixaPort[] = [];
        const totalPorts = caixa.capacidade || 0;
        for (let i = 1; i <= totalPorts; i++) {
          basicPorts.push({
            port_number: i,
            status: 'available'
          });
        }
        setCaixaPorts(basicPorts);
        setOccupiedPorts(0);
      }
    } finally {
      setLoadingPorts(false);
    }
  };

  const loadCircuitosLivres = async (caixaId: number) => {
    try {
      setLoadingCircuitos(true);

      const response = await circuitService.getCircuitos({
        caixa_id: caixaId,
        situacao: 'livre',
        limit: 100
      });

      setCircuitosLivres(response.circuitos || []);
    } catch (error) {
      console.error('Erro ao carregar circuitos livres:', error);
      setCircuitosLivres([]);
    } finally {
      setLoadingCircuitos(false);
    }
  };

  const handleGoBack = () => {
    navigate('/ftth');
  };



  const calculatePortStats = (caixa: FtthCaixa) => {
    console.log('🔄 Calculando estatísticas da Caixa:', caixa);
    const totalPorts = caixa.capacidade || 0; // Tratar null como 0
    const disabledPorts = Array.isArray(caixa.disabled_ports) ? caixa.disabled_ports.length : 0;
    const availablePorts = totalPorts - disabledPorts - occupiedPorts;
    const utilizationPercentage = totalPorts > 0 ? ((disabledPorts + occupiedPorts) / totalPorts) * 100 : 0;



    return {
      totalPorts,
      availablePorts,
      disabledPorts,
      utilizationPercentage
    };
  };



  const getRxPowerColor = (rxPower: number) => {
    if (rxPower >= -23 && rxPower <= 2) {
      return 'success';
    } else if (rxPower >= -27 && rxPower < -23) {
      return 'warning';
    } else if (rxPower < -27) {
      return 'error';
    } else {
      return 'default';
    }
  };

  const getPortIcon = (status: string) => {
    switch (status) {
      case 'available':
        return <CheckCircle color="success" fontSize="small" />;
      case 'occupied':
        return <Warning color="warning" fontSize="small" />;
      case 'disabled':
        return <Block color="error" fontSize="small" />;
      default:
        return <CheckCircle fontSize="small" />;
    }
  };

  const getPortStatus = (port: CaixaPort) => {
    switch (port.status) {
      case 'available':
        return { label: 'Disponível', color: 'success' as const };
      case 'occupied':
        return { label: 'Ocupada', color: 'warning' as const };
      case 'disabled':
        return { label: 'Desabilitada', color: 'error' as const };
      default:
        return { label: 'Desconhecido', color: 'default' as const };
    }
  };

  const formatPortInfo = (port: CaixaPort) => {
    switch (port.status) {
      case 'occupied':
        const clientName = port.info?.client_full_name || port.info?.client_name || 'Cliente';
        return `${clientName}`;
      case 'disabled':
        return port.info?.reason || 'Motivo não informado';
      case 'available':
      default:
        return 'Disponível';
    }
  };

  const handleDisablePort = (portNumber: number) => {
    setSelectedPortToDisable(portNumber);
    setDisableReason('');
    setDisablePortModal(true);
  };

  const handleEnablePort = async (portNumber: number) => {
    if (!caixa) return;

    try {
      setActionLoading(true);

      await ftthCaixaService.changePort(caixa.id, portNumber, 'enable');

      // Atualizar dados da CAIXA e portas
      await loadCaixa();
      await loadCaixaPorts(caixa.id);
      await loadCircuitosLivres(caixa.id);

      setError(null);
    } catch (err: any) {
      console.error('Erro ao habilitar porta:', err);
      setError(`Erro ao habilitar porta ${portNumber}: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const confirmDisablePort = async () => {
    if (!caixa || !selectedPortToDisable || !disableReason.trim()) return;

    try {
      setActionLoading(true);

      await ftthCaixaService.changePort(caixa.id, selectedPortToDisable, 'disable', disableReason.trim());

      // Atualizar dados da CAIXA e portas
      await loadCaixa();
      await loadCaixaPorts(caixa.id);
      await loadCircuitosLivres(caixa.id);

      // Fechar modal
      setDisablePortModal(false);
      setSelectedPortToDisable(null);
      setDisableReason('');
      setError(null);
    } catch (err: any) {
      console.error('Erro ao desabilitar porta:', err);
      setError(`Erro ao desabilitar porta ${selectedPortToDisable}: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const closeDisablePortModal = () => {
    setDisablePortModal(false);
    setSelectedPortToDisable(null);
    setDisableReason('');
  };

  const handleOpenCaixaForm = () => {
    setCaixaFormDialog({
      open: true,
      caixa: caixa
    });
  };

  const handleCloseCaixaForm = () => {
    setCaixaFormDialog({
      open: false,
      caixa: null
    });
  };

  const handleCaixaFormSuccess = (updatedCaixa: FtthCaixa) => {
    console.log('✅ CAIXA atualizada com sucesso, fechando modal');
    handleCloseCaixaForm();
    loadCaixa(); // Recarregar dados da CAIXA
    if (caixa) {
      loadCaixaPorts(caixa.id); // Recarregar portas também
      loadCircuitosLivres(caixa.id); // Recarregar circuitos também
    }
  };

  const handleOpenCircuitoModal = (circuito: Circuito) => {
    setSelectedCircuito(circuito.id);
    setCircuitoModal(true);
  };

  const handleCloseCircuitoModal = () => {
    setCircuitoModal(false);
    setSelectedCircuito(null);
  };

  // === Notificação de clientes ===
  const getOccupiedClientsFromPorts = useCallback(() => {
    const clientMap = new Map<number, { clientId: number; clientName: string; portNumber: number }>();
    caixaPorts.forEach((port) => {
      if (port.status === 'occupied' && port.info?.client_id) {
        if (!clientMap.has(port.info.client_id)) {
          clientMap.set(port.info.client_id, {
            clientId: port.info.client_id,
            clientName: port.info.client_full_name || port.info.client_name || `Cliente #${port.info.client_id}`,
            portNumber: port.port_number
          });
        }
      }
    });
    return Array.from(clientMap.values());
  }, [caixaPorts]);

  const handleOpenNotifyModal = () => {
    setNotifyMensagem('');
    setNotifyGateway(9999);
    setNotifyResults([]);
    setNotifyStep('form');
    setNotifyModalOpen(true);
  };

  const handleCloseNotifyModal = () => {
    if (!notifyLoading) {
      setNotifyModalOpen(false);
    }
  };

  const handleSendNotifications = async () => {
    if (!notifyMensagem.trim()) {
      enqueueSnackbar('Digite uma mensagem para enviar', { variant: 'warning' });
      return;
    }
    if (!notifyGateway) {
      enqueueSnackbar('Selecione um gateway WhatsApp', { variant: 'warning' });
      return;
    }

    const clients = getOccupiedClientsFromPorts();
    if (clients.length === 0) {
      enqueueSnackbar('Nenhum cliente encontrado nesta caixa', { variant: 'warning' });
      return;
    }

    setNotifyLoading(true);
    const results: { clientName: string; success: boolean; error?: string }[] = [];

    for (const client of clients) {
      try {
        // Buscar contatos WhatsApp do cliente
        const contactsResponse = await contactService.getByClientId(client.clientId, {
          limit: 100,
          filters: { is_whatsapp: true, tipo: 'celular', recebe_notificacao: true }
        });

        const whatsappContacts = (contactsResponse.data || []).filter(
          (c: Contact) => c.is_whatsapp && c.tipo === 'celular' && c.recebe_notificacao
        );

        if (whatsappContacts.length === 0) {
          results.push({ clientName: client.clientName, success: false, error: 'Nenhum contato WhatsApp com notificação ativa' });
          continue;
        }

        // Criar mensagem para cada contato WhatsApp do cliente
        for (const contact of whatsappContacts) {
          try {
            await mensagemService.createMensagem({
              tipo: 'whatsapp',
              destino: contact.id,
              conteudo: notifyMensagem.trim(),
              cliente_id: client.clientId,
              gateway_id: notifyGateway === 9999 ? undefined : Number(notifyGateway)
            });
            results.push({ clientName: `${client.clientName} (${contact.nome || contact.numero})`, success: true });
          } catch (err: any) {
            results.push({ clientName: `${client.clientName} (${contact.nome || contact.numero})`, success: false, error: err.message || 'Erro ao cadastrar mensagem' });
          }
        }
      } catch (err: any) {
        results.push({ clientName: client.clientName, success: false, error: err.message || 'Erro ao buscar contatos' });
      }
    }

    setNotifyResults(results);
    setNotifyStep('results');
    setNotifyLoading(false);

    const successCount = results.filter(r => r.success).length;
    const failCount = results.filter(r => !r.success).length;

    if (successCount > 0 && failCount === 0) {
      enqueueSnackbar(`${successCount} mensagem(ns) cadastrada(s) com sucesso!`, { variant: 'success' });
    } else if (successCount > 0 && failCount > 0) {
      enqueueSnackbar(`${successCount} cadastrada(s), ${failCount} falha(s)`, { variant: 'warning' });
    } else {
      enqueueSnackbar('Falha ao cadastrar mensagens', { variant: 'error' });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="outlined" onClick={handleGoBack} startIcon={<ArrowBackIcon />}>
          Voltar
        </Button>
      </Box>
    );
  }

  if (!caixa) {
    return (
      <Box p={3}>
        <Alert severity="warning">
          CAIXA não encontrada
        </Alert>
        <Button variant="outlined" onClick={handleGoBack} startIcon={<ArrowBackIcon />}>
          Voltar
        </Button>
      </Box>
    );
  }

  const portStats = calculatePortStats(caixa);

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100%' }}>
      {/* Header com Breadcrumb */}
      <Box mb={1}>
        <Paper elevation={0} sx={detailPageHeaderPaperSx(theme)}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <IconButton
                onClick={handleGoBack}
                color="primary"
                sx={{
                  bgcolor: 'background.paper',
                  border: `1px solid ${theme.palette.divider}`,
                  boxShadow: 1,
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.15 : 0.08),
                    transform: 'translateY(-1px)',
                    boxShadow: 2,
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <ArrowBackIcon />
              </IconButton>
              <Box>
                <Typography variant="h6" fontWeight={800} sx={{ mb: 0.5, fontSize: '1.05rem', lineHeight: 1.2 }}>
                  {caixa.name}
                </Typography>
                <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 0.5 }}>
                  <Chip
                    icon={getCaixaStatusIcon(caixa.status)}
                    label={getCaixaStatusLabel(caixa.status)}
                    color={getCaixaStatusColor(caixa.status) as any}
                    size="small"
                    variant="outlined"
                  />
                  {caixa.tipo === 'cto' && (
                    <MonitorStatusChip status={caixa.monitor_status ?? 'vazia'} />
                  )}
                </Stack>
              </Box>
            </Box>

            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent="flex-end">
              <Button
                size='small'
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => {
                  loadCaixa();
                  loadCaixaPorts(caixa.id);
                  loadCircuitosLivres(caixa.id);
                }}
              >
                Atualizar
              </Button>
              <Button
                size='small'
                variant="outlined"
                startIcon={<WhatsAppIcon />}
                color="success"
                onClick={handleOpenNotifyModal}
                disabled={caixaPorts.filter(p => p.status === 'occupied' && p.info?.client_id).length === 0}
              >
                Notificar Clientes
              </Button>
              <Button
                size='small'
                variant="contained"
                startIcon={<EditIcon />}
                color="primary"
                onClick={handleOpenCaixaForm}
              >
                Editar
              </Button>
            </Stack>
          </Box>
        </Paper>
      </Box>

      {/* Cards de Informações */}
      <Grid container spacing={1}>
        {/* Card 1: Informações Básicas */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <StorageIcon color="primary" />
                <Typography variant="h6">
                  Informações Básicas
                </Typography>
              </Box>
              <Grid container spacing={1}>
                <Grid item xs={12} md={4}>
                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary">
                      Tipo de rede
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {caixa.tipo_rede === 'propria' ? 'Propria' : 'Neutra'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={8}>
                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary">
                      Endereço
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {caixa.address}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
              <Grid container spacing={1}>
                <Grid item xs={12} md={4}>
                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary">
                      Total de Portas
                    </Typography>
                    <Typography variant="body1" color="primary" fontWeight="bold">
                      {caixa.capacidade}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary">
                      Portas Disponíveis
                    </Typography>
                    <Typography variant="body1" color="success.main" fontWeight="bold">
                      {portStats.availablePorts}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary">
                      Portas Desabilitadas
                    </Typography>
                    <Typography variant="body1" color="error.main" fontWeight="bold">
                      {portStats.disabledPorts}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
              <Box mb={2}>
                <Typography variant="body2" color="text.secondary" mb={1}>
                  Utilização
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={portStats.utilizationPercentage}
                  color={portStats.utilizationPercentage > 80 ? 'error' : 'primary'}
                  sx={{ height: 8, borderRadius: 4 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {portStats.utilizationPercentage.toFixed(1)}%
                </Typography>
              </Box>

              {caixa.tipo === 'cto' && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                    Monitoramento
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={6}>
                      <Box mb={2}>
                        <Typography variant="body2" color="text.secondary">
                          Status
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                          <MonitorStatusChip status={caixa.monitor_status ?? 'vazia'} />
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box mb={2}>
                        <Typography variant="body2" color="text.secondary">
                          Última verificação
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {formatMonitorDateTime(caixa.monitor_ultima_verificacao_em)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box mb={2}>
                        <Typography variant="body2" color="text.secondary">
                          Última vez online
                        </Typography>
                        <Typography variant="body1" fontWeight="medium" color="success.main">
                          {formatMonitorDateTime(caixa.monitor_ultimo_online_em)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box mb={2}>
                        <Typography variant="body2" color="text.secondary">
                          Última vez offline
                        </Typography>
                        <Typography variant="body1" fontWeight="medium" color="error.main">
                          {formatMonitorDateTime(caixa.monitor_ultimo_offline_em)}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </>
              )}

              {caixa.pon_port ? (
                <>
                  <Grid container spacing={1}>
                    <Grid item xs={12} md={6}>
                      <Box mb={2}>
                        <Typography variant="body2" color="text.secondary">
                          OLT
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {caixa.pon_port.olt?.name ? (
                            <Link
                              component="button"
                              variant="body1"
                              onClick={() => navigate(`/ftth/olts/${caixa.pon_port!.olt!.id}`)}
                              sx={{ textDecoration: 'none', color: 'primary.main', fontWeight: 'medium' }}
                            >
                              {caixa.pon_port.olt.name}
                            </Link>
                          ) : (
                            'N/A'
                          )}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <Box mb={2}>
                        <Typography variant="body2" color="text.secondary">
                          Porta
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {caixa.pon_port?.id || caixa.port_id ? (
                            <Link
                              component="button"
                              variant="body1"
                              onClick={() => setCtosModalOpen(true)}
                              sx={{ textDecoration: 'none', color: 'primary.main', fontWeight: 'medium' }}
                            >
                              {caixa.pon_port?.sfp || caixa.pon_port?.portNumber || `Porta ${caixa.port_id}`}
                            </Link>
                          ) : (
                            'N/A'
                          )}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          IP da OLT
                        </Typography>
                        <Typography variant="body1" fontFamily="monospace">
                          {caixa.pon_port.olt?.ip_address || 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Porta PON não vinculada
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={8}>
          {/* Seção do Mapa */}
          {caixa.latitude && caixa.longitude && (
            <Box sx={{ mb: 2 }}>
              <CaixaMapComponent
                latitude={caixa.latitude}
                longitude={caixa.longitude}
                caixaName={caixa.name}
                address={caixa.address}
                height={500}

              />

              <Box display="flex" justifyContent="center" mt={-3}>
                <Typography variant="caption" color="text.secondary">
                  📍 {caixa.latitude.toFixed(6)}, {caixa.longitude.toFixed(6)} •{' '}
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => {
                      const url = `https://www.google.com/maps?q=${caixa.latitude},${caixa.longitude}`;
                      window.open(url, '_blank');
                    }}
                    sx={{ textTransform: 'none', p: 0, minWidth: 'auto' }}
                  >
                    Abrir no Google Maps
                  </Button>
                </Typography>
              </Box>
            </Box>
          )}
        </Grid>
      </Grid>
      {/* Tabela de Portas */}
      <Card sx={{ mb: 1 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="h6" fontWeight="bold">
              🔌 Lista de Portas ({caixa.capacidade} portas)
            </Typography>

            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => {
                loadCaixaPorts(caixa.id);
                loadCircuitosLivres(caixa.id);
              }}
              disabled={loadingPorts}
              size="small"
            >
              Atualizar Portas
            </Button>
          </Box>

          {loadingPorts && <LinearProgress sx={{ mb: 1 }} />}

          <TableContainer component={Paper}
            sx={{
              borderRadius: 2,
              boxShadow: (theme) => theme.shadows[1],
              '& .MuiTable-root': {
                minWidth: 650,
              },
            }} variant="outlined">
            <Table size="small" >
              <TableHead>
                <TableRow sx={tableHeadRowSx(theme)}>
                  <TableCell align="center">
                    Porta
                  </TableCell>
                  <TableCell>
                    Status
                  </TableCell>
                  <TableCell>
                    Informações
                  </TableCell>
                  <TableCell>
                    Status do Serviço
                  </TableCell>
                  <TableCell>
                    Conexão
                  </TableCell>
                  <TableCell>
                    Sinal RX
                  </TableCell>
                  <TableCell>
                    Circuito
                  </TableCell>
                  <TableCell align="center">
                    Ações
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {caixaPorts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        {loadingPorts ? 'Carregando portas...' : 'Nenhum dado de porta disponível'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  caixaPorts.map((port) => {
                    const portStatus = getPortStatus(port);
                    return (
                      <TableRow key={port.port_number} hover>
                        <TableCell align="center">
                          <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                            <Avatar
                              sx={{
                                width: 25,
                                height: 25,
                                bgcolor: portStatus.color === 'success' ? 'success.main'
                                  : portStatus.color === 'warning' ? 'warning.main'
                                    : portStatus.color === 'error' ? 'error.main'
                                      : 'text.disabled',
                                fontSize: '0.875rem',
                                fontWeight: 'bold'
                              }}
                            >
                              {port.port_number}
                            </Avatar>
                          </Box>
                        </TableCell>

                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            {getPortIcon(port.status)}
                            <Chip
                              label={portStatus.label}
                              color={portStatus.color}
                              size="small"
                              variant="outlined"
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {port.status === 'occupied' && port.info?.client_id ? (
                              <Link
                                component="button"
                                variant="body2"
                                onClick={() => navigate(`/clients/${port.info?.client_id}`)}
                                sx={{ textDecoration: 'none', textAlign: 'left', fontWeight: 'medium' }}
                              >
                                {formatPortInfo(port)}
                              </Link>
                            ) : (
                              formatPortInfo(port)
                            )}
                          </Typography>
                          {port.status === 'occupied' && port.info?.contract_id && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Contrato:{' '}
                              <Link
                                component="button"
                                variant="caption"
                                onClick={() => navigate(`/contracts/${port.info?.contract_id}`)}
                                sx={{ textDecoration: 'none', color: 'inherit' }}
                              >
                                {port.info.contract_title}
                              </Link>
                            </Typography>
                          )}
                          {port.status === 'disabled' && port.info?.date && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Desabilitada em: {new Date(port.info.date).toLocaleString()}
                            </Typography>
                          )}
                        </TableCell>

                        <TableCell>
                          {port.status === 'occupied' ? (
                            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                              <Chip
                                label={normalizeStatusLabel(port.info?.service_status)}
                                color={getServiceStatusColor(port.info?.service_status)}
                                size="small"
                                sx={{ fontWeight: 600, height: 22, fontSize: '0.68rem' }}
                              />
                              {port.info?.service_type && (
                                <Typography variant="caption" color="text.secondary">
                                  {normalizeStatusLabel(port.info.service_type)}
                                </Typography>
                              )}
                            </Stack>
                          ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem' }}>-</Typography>
                          )}
                        </TableCell>

                        <TableCell>
                          {port.status === 'occupied' ? (
                            <Chip
                              label={port.info?.connection_status || 'N/A'}
                              color={getConnectionStatusColor(port.info?.connection_status)}
                              size="small"
                              variant={port.info?.connection_status === 'N/A' ? 'outlined' : 'filled'}
                              sx={{ fontWeight: 700, minWidth: 64, height: 22, fontSize: '0.68rem' }}
                            />
                          ) : (
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.78rem' }}>-</Typography>
                          )}
                        </TableCell>

                        <TableCell>
                          {port.status === 'occupied' && (port.info?.modelo_tipo === 'ONU' || port.info?.modelo_tipo === 'ONT') && (port.info?.rxPower !== null && port.info?.rxPower !== undefined) ? (
                            <Chip
                              label={`${port.info.rxPower} dBm`}
                              color={getRxPowerColor(port.info.rxPower) as any}
                              size="small"
                              variant="outlined"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          ) : port.status === 'occupied' && (port.info?.modelo_tipo === 'ONU' || port.info?.modelo_tipo === 'ONT') ? (
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>N/A</Typography>
                          ) : null}
                        </TableCell>

                        <TableCell>
                          {port.info?.circuito_identificacao && port.info?.circuito_id ? (
                            <Chip
                              label={`Circuito ${port.info.circuito_identificacao}`}
                              color={port.status === 'occupied' ? 'error' : 'success'}
                              variant="filled"
                              size="small"
                              sx={{
                                fontWeight: 'medium',
                                '& .MuiChip-label': {
                                  px: 1
                                },
                                cursor: 'pointer'
                              }}
                              onClick={async () => {
                                try {
                                  const c = await circuitService.getCircuito(port.info!.circuito_id!);
                                  handleOpenCircuitoModal(c);
                                } catch (e) {
                                  console.error('Erro ao abrir circuito', e);
                                }
                              }}
                            />
                          ) : (
                            <Typography variant="body2">
                              {port.info?.circuito_identificacao ? `${port.info.circuito_identificacao}` : ''}
                            </Typography>
                          )}
                        </TableCell>

                        <TableCell align="center">
                          <Box display="flex" gap={1} justifyContent="center">
                            {port.status === 'disabled' ? (
                              <Tooltip title="Habilitar porta">
                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={() => handleEnablePort(port.port_number)}
                                  disabled={actionLoading}
                                >
                                  <Power fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : port.status === 'occupied' ? (
                              <Tooltip title="Porta ocupada - não pode ser desabilitada">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="default"
                                    disabled={true}
                                  >
                                    <PowerOff fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            ) : (
                              <Tooltip title="Desabilitar porta">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDisablePort(port.port_number)}
                                  disabled={actionLoading}
                                >
                                  <PowerOff fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Seção de Circuitos Livres */}
      <Card sx={{ mb: 1 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" fontWeight="bold">
              🔌 Circuitos Livres ({circuitosLivres.length})
            </Typography>

            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => loadCircuitosLivres(caixa.id)}
              disabled={loadingCircuitos}
              size="small"
            >
              Atualizar Circuitos
            </Button>
          </Box>

          {loadingCircuitos && <LinearProgress sx={{ mb: 2 }} />}

          {circuitosLivres.length === 0 ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              py={2}
              sx={{
                bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.06 : 0.04),
                borderRadius: 1,
                border: `1px dashed ${theme.palette.divider}`,
              }}
            >
              <Typography color="text.secondary">
                {loadingCircuitos ? 'Carregando circuitos...' : 'Nenhum circuito livre encontrado nesta CAIXA'}
              </Typography>
            </Box>
          ) : (
            <Box display="flex" flexWrap="wrap" gap={1}>
              {circuitosLivres.map((circuito) => (
                <Chip
                  key={circuito.id}
                  label={`Circuito ${circuito.identificacao}`}
                  color="success"
                  variant="filled"
                  sx={{
                    fontWeight: 'medium',
                    '& .MuiChip-label': {
                      px: 2
                    }
                  }}
                  onClick={() => handleOpenCircuitoModal(circuito)}
                />
              ))}
            </Box>
          )}

          {circuitosLivres.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              💡 Circuitos disponíveis para uso nesta CAIXA
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Modal para Formulário CAIXA */}
      <Dialog
        open={caixaFormDialog.open}
        onClose={handleCloseCaixaForm}
        maxWidth="md"
        fullWidth
      >
        <DialogContent sx={{ p: 3 }}>
          {caixaFormDialog.caixa?.tipo === 'ceo' ? (
            <FtthCaixaCeoForm
              caixa={caixaFormDialog.caixa || undefined}
              onSuccess={handleCaixaFormSuccess}
              onCancel={handleCloseCaixaForm}
            />
          ) : (
            <CaixaForm
              caixa={caixaFormDialog.caixa || undefined}
              onSuccess={handleCaixaFormSuccess}
              onCancel={handleCloseCaixaForm}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Modal para Desabilitar Porta */}
      <Dialog
        open={disablePortModal}
        onClose={closeDisablePortModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Desabilitar Porta {selectedPortToDisable}
            </Typography>
            <IconButton onClick={closeDisablePortModal} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Informe o motivo para desabilitar esta porta. Esta ação impedirá que novos equipamentos sejam conectados à porta.
          </Typography>

          <TextField
            autoFocus
            fullWidth
            label="Motivo da desabilitação"
            multiline
            rows={3}
            value={disableReason}
            onChange={(e) => setDisableReason(e.target.value)}
            placeholder="Ex: Cabo danificado, Manutenção preventiva, etc..."
            error={disableReason.trim().length === 0}
            helperText={disableReason.trim().length === 0 ? 'Motivo é obrigatório' : ''}
          />
        </DialogContent>

        <DialogActions>
          <Button
            onClick={closeDisablePortModal}
            disabled={actionLoading}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmDisablePort}
            disabled={actionLoading || disableReason.trim().length === 0}
            startIcon={actionLoading ? <CircularProgress size={16} /> : <PowerOff />}
          >
            {actionLoading ? 'Desabilitando...' : 'Desabilitar Porta'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal para Informações do Circuito */}
      {selectedCircuito && (
        <CircuitoViewModal
          open={circuitoModal}
          onClose={handleCloseCircuitoModal}
          circuitoId={selectedCircuito}
        />
      )}

      {/* Modal CTOs da porta PON vinculada */}
      {(caixa?.pon_port?.id ?? caixa?.port_id) && (
        <CtosModal
          open={ctosModalOpen}
          onClose={() => setCtosModalOpen(false)}
          cardSlot={caixa.pon_port?.slot}
          portNumber={caixa.pon_port?.sfp || caixa.pon_port?.portNumber}
          portId={caixa.pon_port?.id ?? caixa.port_id}
        />
      )}

      {/* Modal de Notificação de Clientes */}
      <Dialog
        open={notifyModalOpen}
        onClose={handleCloseNotifyModal}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={notifyLoading}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={1}>
              <WhatsAppIcon color="success" />
              <Typography variant="h6" fontWeight="bold">
                Notificar Clientes
              </Typography>
            </Box>
            <IconButton onClick={handleCloseNotifyModal} size="small" disabled={notifyLoading}>
              <Close />
            </IconButton>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {caixa.name} — {getOccupiedClientsFromPorts().length} cliente(s) conectado(s)
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
          {notifyStep === 'form' ? (
            <Box>
              {/* Lista de clientes */}
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                <PersonIcon sx={{ fontSize: '1rem', verticalAlign: 'middle', mr: 0.5 }} />
                Clientes que serão notificados:
              </Typography>
              <Box
                sx={{
                  maxHeight: 150,
                  overflowY: 'auto',
                  mb: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  bgcolor: alpha(theme.palette.background.default, 0.5),
                }}
              >
                <List dense disablePadding>
                  {getOccupiedClientsFromPorts().map((client) => (
                    <ListItem key={client.clientId} sx={{ py: 0.25 }}>
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <PersonIcon fontSize="small" color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={client.clientName}
                        secondary={`Porta ${client.portNumber}`}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>

              {/* Gateway */}
              <Box sx={{ mb: 2 }}>
                <GatewayWhatsappSelect
                  value={notifyGateway}
                  onChange={setNotifyGateway}
                  label="Gateway WhatsApp"
                  required
                  disabled={notifyLoading}
                  size="small"
                  helperText="Selecione o gateway para envio das mensagens"
                />
              </Box>

              {/* Mensagem */}
              <Box>
                <SysEditorWhatsapp
                  value={notifyMensagem}
                  onChange={setNotifyMensagem}
                  placeholder="Digite a mensagem que será enviada para todos os clientes..."
                  maxLength={1000}
                  disabled={notifyLoading}
                  showPreview={true}
                  rows={4}
                  label="Mensagem *"
                  helperText="Use formatação *negrito* e _itálico_ para destacar texto"
                />
              </Box>

              {notifyLoading && (
                <Box display="flex" alignItems="center" gap={1} mt={2}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Cadastrando mensagens...
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            /* Resultados */
            <Box>
              <Alert
                severity={notifyResults.every(r => r.success) ? 'success' : notifyResults.some(r => r.success) ? 'warning' : 'error'}
                sx={{ mb: 2 }}
              >
                {notifyResults.filter(r => r.success).length} mensagem(ns) cadastrada(s) com sucesso
                {notifyResults.filter(r => !r.success).length > 0 &&
                  `, ${notifyResults.filter(r => !r.success).length} falha(s)`}
              </Alert>

              <List dense>
                {notifyResults.map((result, index) => (
                  <ListItem key={index} sx={{ py: 0.25 }}>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      {result.success ? (
                        <CheckCircleOutlineIcon fontSize="small" color="success" />
                      ) : (
                        <ErrorOutlineIcon fontSize="small" color="error" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={result.clientName}
                      secondary={result.success ? 'Mensagem cadastrada' : result.error}
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                      secondaryTypographyProps={{
                        variant: 'caption',
                        color: result.success ? 'success.main' : 'error.main'
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          {notifyStep === 'form' ? (
            <>
              <Button onClick={handleCloseNotifyModal} disabled={notifyLoading}>
                Cancelar
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={handleSendNotifications}
                disabled={notifyLoading || !notifyMensagem.trim() || !notifyGateway || getOccupiedClientsFromPorts().length === 0}
                startIcon={notifyLoading ? <CircularProgress size={18} /> : <NotificationsIcon />}
              >
                {notifyLoading ? 'Cadastrando...' : 'Cadastrar Mensagens'}
              </Button>
            </>
          ) : (
            <Button variant="contained" onClick={handleCloseNotifyModal}>
              Fechar
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FtthCaixaDetailsPage; 